import { SanityClient, createClient } from '@sanity/client'

export const sanityProjectId = process.env
  .NEXT_PUBLIC_SANITY_PROJECT_ID as string
export const sanityDataset = process.env.NEXT_PUBLIC_SANITY_DATASET as string
export const sanityApiVersion = 'v2023-08-01'

if (!sanityProjectId) {
  throw new Error('Missing NEXT_PUBLIC_SANITY_PROJECT_ID')
}

if (!sanityDataset) {
  throw new Error('Missing NEXT_PUBLIC_SANITY_DATASET')
}

/**
 * Gets Sanity client.
 */
export const getSanityClient = () => {
  return createClient({
    projectId: sanityProjectId,
    dataset: sanityDataset,
    apiVersion: sanityApiVersion,
    useCdn: process.env.NEXT_PUBLIC_APP_ENV === 'production',
    perspective: 'published',
  })
}

/**
 * Gets Sanity client with preview token.
 */
export const getPreviewSanityClient = (token: string) => {
  return createClient({
    projectId: sanityProjectId,
    dataset: sanityDataset,
    token,
    apiVersion: sanityApiVersion,
    useCdn: false,
    perspective: 'drafts',
    ignoreBrowserTokenWarning: true,
  })
}

const minifyQuery = (query: string) => {
  // Remove multiple spaces and newlines in a row
  let minifiedQuery = query.replace(/\s{2,}/g, ' ')

  // Trim leading and trailing spaces and newlines
  minifiedQuery = minifiedQuery.replace(/^\s+|\s+$/g, '')

  // Remove spaces before and after special characters
  minifiedQuery = minifiedQuery
    .replaceAll(' == ', '==')
    .replaceAll(' != ', '!=')
    .replaceAll(' => ', '=>')
    .replaceAll(' && ', '&&')
    .replaceAll(' || ', '||')
    .replaceAll(' | ', '|')
    .replaceAll(') [', ')[')
    .replaceAll('] {', ']{')
    .replaceAll(' {', '{')
    .replaceAll('{ ', '{')
    .replaceAll(' }', '}')
    .replaceAll('[ ', '[')
    .replaceAll(' ]', ']')
    .replaceAll(': ', ':')
    .replaceAll(', ', ',')

  // Remove commas before closing curly braces
  minifiedQuery = minifiedQuery.replaceAll(',}', '}')

  return minifiedQuery
}

/**
 * Fetches data from Sanity using a GROQ query.
 */
export const fetchQuery = async <T, P = Record<string, never>>(
  sanityClient: SanityClient,
  query: string,
  params?: P,
) => {
  const minifiedQuery = minifyQuery(query)

  return sanityClient.fetch<T>(minifiedQuery, params ?? {})
}
